import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Download, ArrowLeft, FileSpreadsheet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExportPage = () => {
  const navigate = useNavigate();
  const [isDownloadingContacts, setIsDownloadingContacts] = useState(false);
  const [isDownloadingNewsletter, setIsDownloadingNewsletter] = useState(false);
  const [isDownloadingOrders, setIsDownloadingOrders] = useState(false);

  const handleExportContacts = async () => {
    setIsDownloadingContacts(true);
    
    try {
      // Download CSV file
      window.open(`${API}/contact/export`, '_blank');
      
      setTimeout(() => {
        setIsDownloadingContacts(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setIsDownloadingContacts(false);
      alert('Error downloading file. Please try again.');
    }
  };

  const handleExportNewsletter = async () => {
    setIsDownloadingNewsletter(true);
    
    try {
      // Download CSV file
      window.open(`${API}/newsletter/export`, '_blank');
      
      setTimeout(() => {
        setIsDownloadingNewsletter(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setIsDownloadingNewsletter(false);
      alert('Error downloading file. Please try again.');
    }
  };

  const handleExportOrders = async () => {
    setIsDownloadingOrders(true);
    
    try {
      // Download CSV file
      window.open(`${API}/orders/export`, '_blank');
      
      setTimeout(() => {
        setIsDownloadingOrders(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setIsDownloadingOrders(false);
      alert('Error downloading file. Please try again.');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--cream-white)' }}>
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 
            className="text-2xl font-bold"
            style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
          >
            Contact Inquiries Export
          </h1>
          <Button 
            variant="outline"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            style={{ borderColor: 'var(--warm-gray)', color: 'var(--charcoal)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </nav>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Card 
          className="border-0"
          style={{ 
            backgroundColor: 'white',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            borderRadius: '8px'
          }}
        >
          <CardHeader className="text-center pb-8 pt-12">
            <div 
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--warm-sage)' }}
            >
              <FileSpreadsheet className="w-10 h-10" style={{ color: 'var(--cream-white)' }} />
            </div>
            <CardTitle 
              className="text-3xl font-light mb-3"
              style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
            >
              Export Data
            </CardTitle>
            <CardDescription style={{ color: 'var(--warm-gray)', fontSize: '16px' }}>
              Download contact inquiries and newsletter subscriptions as CSV files
            </CardDescription>
          </CardHeader>

          <CardContent className="pb-12">
            <div className="space-y-8">
              {/* Contact Inquiries Section */}
              <div className="space-y-4">
                <h3 className="text-xl font-medium" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
                  1. Contact Inquiries
                </h3>
                <div className="bg-gray-50 rounded-lg p-6" style={{ backgroundColor: 'var(--soft-gray)' }}>
                  <h4 className="font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
                    Includes:
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--warm-gray)' }}>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Customer name, email, and phone number</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Product they inquired about</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Complete message and inquiry details</span>
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={handleExportContacts}
                  disabled={isDownloadingContacts}
                  size="lg"
                  className="w-full uppercase tracking-wider"
                  style={{ 
                    backgroundColor: 'var(--warm-sage)',
                    color: 'var(--cream-white)',
                    letterSpacing: '1.5px',
                    padding: '20px',
                    height: 'auto'
                  }}
                >
                  {isDownloadingContacts ? (
                    'Downloading...'
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Download Contact Inquiries
                    </>
                  )}
                </Button>
              </div>

              {/* Newsletter Subscriptions Section */}
              <div className="space-y-4">
                <h3 className="text-xl font-medium" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
                  2. Newsletter Subscriptions
                </h3>
                <div className="bg-gray-50 rounded-lg p-6" style={{ backgroundColor: 'var(--soft-gray)' }}>
                  <h4 className="font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
                    Includes:
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--warm-gray)' }}>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Subscriber email addresses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Subscription date and time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Subscription status (active/unsubscribed)</span>
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={handleExportNewsletter}
                  disabled={isDownloadingNewsletter}
                  size="lg"
                  className="w-full uppercase tracking-wider"
                  style={{ 
                    backgroundColor: 'var(--rich-chocolate)',
                    color: 'var(--cream-white)',
                    letterSpacing: '1.5px',
                    padding: '20px',
                    height: 'auto'
                  }}
                >
                  {isDownloadingNewsletter ? (
                    'Downloading...'
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Download Newsletter Subscribers
                    </>
                  )}
                </Button>
              </div>

              {/* Orders Section */}
              <div className="space-y-4">
                <h3 className="text-xl font-medium" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
                  3. Bestellungen
                </h3>
                <div className="bg-gray-50 rounded-lg p-6" style={{ backgroundColor: 'var(--soft-gray)' }}>
                  <h4 className="font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
                    Enthält:
                  </h4>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--warm-gray)' }}>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Vorname, Name, Adresse, PLZ, Wohnort</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>E-Mail-Adresse und Telefonnummer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Produkttitel und Preis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span>Bestelldatum und Status</span>
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={handleExportOrders}
                  disabled={isDownloadingOrders}
                  size="lg"
                  className="w-full uppercase tracking-wider"
                  style={{ 
                    backgroundColor: 'var(--lv-brown)',
                    color: 'var(--cream-white)',
                    letterSpacing: '1.5px',
                    padding: '20px',
                    height: 'auto'
                  }}
                >
                  {isDownloadingOrders ? (
                    'Downloading...'
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Bestellungen herunterladen
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <div className="mt-8 p-6 bg-white rounded-lg" style={{ border: '1px solid var(--soft-taupe)' }}>
          <h4 className="font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
            💡 How to Use:
          </h4>
          <ol className="space-y-2 text-sm" style={{ color: 'var(--warm-gray)' }}>
            <li>1. Click "Download CSV File" button above</li>
            <li>2. Open the downloaded file in Excel, Google Sheets, or Numbers</li>
            <li>3. Sort, filter, and manage your inquiries as needed</li>
            <li>4. Contact customers directly using their email or phone</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ExportPage;
